import React from 'react';
import clsx from 'clsx';
import { useSelector } from 'react-redux';
import { useRoutes } from 'react-router-dom';
import { styled } from '@mui/material';

import FuseDialog from '@fuse/core/FuseDialog';
import FuseMessage from '@fuse/core/FuseMessage';
import FuseSuspense from '@fuse/core/FuseSuspense';
import AppContext from 'app/AppContext';
import SettingsPanel from 'app/fuse-layouts/shared-components/SettingsPanel';
import FooterLayout2 from './components/FooterLayout2';
import LeftSideLayout2 from './components/LeftSideLayout2';
import NavbarWrapperLayout2 from './components/NavbarWrapperLayout2';
import RightSideLayout2 from './components/RightSideLayout2';
import ToolbarLayout2 from './components/ToolbarLayout2';

interface RootProps {
	mode: 'boxed' | 'container';
	containerWidth: number;
}

const Root = styled('div')<RootProps>(({ containerWidth, mode }) => ({
	...(mode === 'boxed' && {
		clipPath: 'inset(0)',
		maxWidth: `${containerWidth}px`,
		margin: '0 auto',
		boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
	}),
	...(mode === 'container' && {
		'& .container': {
			maxWidth: `${containerWidth}px`,
			width: '100%',
			margin: '0 auto'
		}
	})
}));

function Layout2({ children }: { children?: React.ReactNode }) {
	const config = useSelector(({ fuse }: any) => fuse.settings.current.layout.config);
	const appContext = React.useContext(AppContext);
	const { routes } = appContext;

	return (
		<Root id="fuse-layout" className="flex w-full" containerWidth={config.containerWidth} mode={config.mode}>
			{config.leftSidePanel.display && <LeftSideLayout2 />}

			<div className="flex min-w-0 flex-auto flex-col">
				<main id="fuse-main" className="relative flex min-h-screen min-w-0 flex-auto flex-col">
					{config.navbar.display && (
						<NavbarWrapperLayout2
							className={clsx(config.navbar.style === 'fixed' ? 'sticky top-0 z-50' : '')}
						/>
					)}

					{config.toolbar.display && (
						<ToolbarLayout2
							className={clsx(
								config.toolbar.style === 'fixed' && 'sticky top-0',
								config.toolbar.position === 'above' && 'z-40 order-first'
							)}
						/>
					)}

					<div className="sticky top-0 z-99">
						<SettingsPanel />
					</div>

					<div className="relative z-10 flex min-h-0 flex-auto flex-col">
						<FuseDialog />

						<FuseSuspense>{useRoutes(routes)}</FuseSuspense>

						{children}
					</div>

					{config.footer.display && (
						<FooterLayout2 className={config.footer.style === 'fixed' ? 'sticky bottom-0' : ''} />
					)}
				</main>
			</div>

			{config.rightSidePanel.display && <RightSideLayout2 />}
			<FuseMessage />
		</Root>
	);
}

export default React.memo(Layout2);
